/** Type-specific status pipelines — shared with UI */

export const CLOSED_STATUSES = ['client_not_interested', 'booking_cancelled'];

export const STATUS_PIPELINES = {
  visa: ['new', 'details_provided', 'visa_applied', 'visa_issued'],
  tour: [
    'new',
    'quotation',
    'tour_accepted_advance',
    'tour_accepted_progress',
    'tour_finalised',
  ],
  ticket: ['new', 'quotation_sent', 'temporary_booking', 'ticket_issued'],
};

const DEFAULT_PIPELINES = structuredClone(STATUS_PIPELINES);
let runtimePipelines = structuredClone(STATUS_PIPELINES);

export function applyPipelineSettings(custom) {
  runtimePipelines = structuredClone(DEFAULT_PIPELINES);
  if (!custom || typeof custom !== 'object') return;
  for (const type of ['visa', 'tour', 'ticket']) {
    if (Array.isArray(custom[type]) && custom[type].length && custom[type].every((s) => typeof s === 'string')) {
      runtimePipelines[type] = custom[type];
    }
  }
}

export const COMPLETED_BY_TYPE = {
  visa: 'visa_issued',
  tour: 'tour_finalised',
  ticket: 'ticket_issued',
};

export const STATUS_BUCKETS = {
  active: { label: 'Active', filterKey: 'bucket:active' },
  waiting_client: { label: 'Waiting on client', filterKey: 'bucket:waiting_client', statuses: ['quotation', 'quotation_sent', 'temporary_booking'] },
  waiting_supplier: { label: 'Waiting on supplier', filterKey: 'bucket:waiting_supplier', statuses: [] },
  completed: { label: 'Completed', filterKey: 'bucket:completed', statuses: ['visa_issued', 'tour_finalised', 'ticket_issued'] },
  closed: { label: 'Closed', filterKey: 'bucket:closed', statuses: CLOSED_STATUSES },
};

export const STATUS_LABELS = {
  new: 'New inquiry',
  details_provided: 'Details provided',
  visa_applied: 'Visa applied',
  visa_issued: 'Visa issued',
  quotation: 'Quotation',
  tour_accepted_advance: 'Confirmed & advance',
  tour_accepted_progress: 'Processing (TKT & visa)',
  tour_finalised: 'Finalised',
  quotation_sent: 'Quotation sent',
  temporary_booking: 'Temporary booking',
  booking_confirmed: 'Booking confirmed',
  ticket_issued: 'Ticket issued',
  client_not_interested: 'Client not interested',
  booking_cancelled: 'Booking cancelled',
};

export const STATUS_BADGE_CLS = {
  new: 'badge-new',
  details_provided: 'badge-contacted',
  visa_applied: 'badge-followup',
  visa_issued: 'badge-converted',
  quotation: 'badge-quoted',
  tour_accepted_advance: 'badge-confirmed',
  tour_accepted_progress: 'badge-followup',
  tour_finalised: 'badge-converted',
  quotation_sent: 'badge-quoted',
  temporary_booking: 'badge-confirmed',
  booking_confirmed: 'badge-confirmed',
  ticket_issued: 'badge-converted',
  client_not_interested: 'badge-not_interested',
  booking_cancelled: 'badge-not_interested',
};

export const PIPELINE_COLORS = {
  new: '#00AEEF',
  details_provided: '#4fd6e8',
  visa_applied: '#b89aef',
  visa_issued: '#3ddeb0',
  quotation: '#8A63D2',
  tour_accepted_advance: '#f59e0b',
  tour_accepted_progress: '#b89aef',
  tour_finalised: '#3ddeb0',
  quotation_sent: '#8A63D2',
  temporary_booking: '#f59e0b',
  booking_confirmed: '#f59e0b',
  ticket_issued: '#3ddeb0',
  client_not_interested: '#94a3b8',
};

export const STAGE_HINTS = {
  'visa:details_provided': 'Enter the quoted price for this visa inquiry.',
  'visa:visa_applied': 'Record the application ID and passport number.',
  'visa:visa_issued': 'Visa has been issued. Add issue date or notes if needed.',
  'ticket:quotation_sent': 'Enter quote sent date/time, airline, and per-person cost + profit rates.',
  'ticket:quotation_sent_v2': 'Record when the quote was sent and add any notes about what you sent to the client.',
  'ticket:temporary_booking': 'Enter PNR, booking system, and hold expiry — countdown starts on save.',
  'ticket:temporary_booking_v2': 'Enter pricing, PNR, where you booked, and hold expiry.',
  'ticket:ticket_issued': 'Record issue date and payment — inquiry closes and accounting updates.',
  'tour:quotation': 'Enter supplier cost and client quote per person (flight/visa optional).',
  'tour:tour_accepted_advance': 'Client confirmed — record travel date and advance payment.',
  'tour:tour_accepted_progress': 'Track progress — fill ticket & visa dates when done to auto-finalise.',
  'tour:tour_finalised': 'Tour closed — optional closing notes.',
  'visa:client_not_interested': 'Why is the client not interested?',
  'tour:client_not_interested': 'Why is the client not interested?',
  'ticket:client_not_interested': 'Client declined or asked to pause — note for future contact.',
  'ticket:booking_cancelled': 'TTL expired or client did not pay — record penalty and refund.',
};

export function buildStatusCfg() {
  const cfg = {};
  for (const [key, label] of Object.entries(STATUS_LABELS)) {
    cfg[key] = { label, cls: STATUS_BADGE_CLS[key] || 'badge-new' };
  }
  cfg.lost = { label: 'Client not interested', cls: 'badge-not_interested' };
  return cfg;
}

export const STATUS_CFG = buildStatusCfg();

export function getPipeline(type) {
  return runtimePipelines[type] || runtimePipelines.tour;
}

export function getStatusesForType(type, { includeClosed = true } = {}) {
  const pipe = [...getPipeline(type)];
  if (includeClosed) CLOSED_STATUSES.forEach((s) => pipe.push(s));
  return pipe;
}

export function getStatusOrder(type) {
  return getStatusesForType(type);
}

/** Flat list for filters that span all types */
export const STATUS_ORDER = [...new Set([
  ...Object.values(runtimePipelines).flat(),
  ...CLOSED_STATUSES,
])];

export function isClosedStatus(status) {
  return CLOSED_STATUSES.includes(status);
}

export function isCompletedStatus(type, status) {
  return COMPLETED_BY_TYPE[type] === status;
}

export function isTerminalStatus(type, status) {
  return isClosedStatus(status) || isCompletedStatus(type, status);
}

export function statusBucket(type, status) {
  if (CLOSED_STATUSES.includes(status)) return 'closed';
  if (isCompletedStatus(type, status)) return 'completed';
  if (STATUS_BUCKETS.waiting_client.statuses.includes(status)) return 'waiting_client';
  if (STATUS_BUCKETS.waiting_supplier.statuses.includes(status)) return 'waiting_supplier';
  return 'active';
}

export function bucketStatuses(bucketKey) {
  const b = STATUS_BUCKETS[bucketKey];
  if (!b) return [];
  if (b.statuses) return b.statuses;
  if (bucketKey === 'active') {
    const completed = Object.values(COMPLETED_BY_TYPE);
    return STATUS_ORDER.filter((s) => !CLOSED_STATUSES.includes(s) && !completed.includes(s));
  }
  return [];
}

export function getStageHint(type, status) {
  return STAGE_HINTS[`${type}:${status}`]
    || STAGE_HINTS[`${type}:client_not_interested`]
    || `Update to ${STATUS_LABELS[status] || status}`;
}
